import { useState, useEffect, useMemo } from 'react';
import { Search, Pencil, Plus, X } from 'lucide-react';
import { spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { formatMoney } from '../utils/currencyUtil';
import {
  subscribePosCategories, subscribePosProducts, subscribePosOrders,
  addPosCategory, removePosCategory, posStockLevel,
  subscribePosCategoryIcons, setPosCategoryIcon, posIconIsIconify, posIconifyUrl,
  sortPosProducts, tallyPosUnitsSold, posProductPageSize,
  describePosUnits, posReserveUnits, posReserveEnabled,
} from '../utils/posUtil';
import {
  POS_ALL_CATEGORIES,
  POS_SEARCH_MIN_CHARS,
  POS_INVENTORY_VIEWS,
  POS_INVENTORY_VIEW_DEFAULT,
  POS_STOCK_SCOPES,
  POS_STOCK_SCOPE_DEFAULT,
  POS_SORT_BY_SOLD,
  POS_SORT_BY_CUSTOM,
  POS_CUSTOM_SORT_LISTS,
  POS_SORT_PREFERENCE_SCREENS,
} from '../constants/posConst';
import { posCardGridAttrs } from '../utils/posCardSizeUtil';
import useCustomSort from '../hooks/useCustomSort';
import useSavedSort from '../hooks/useSavedSort';
import useDragReorder from '../hooks/useDragReorder';
import PosSortBar from './PosSortBar';
import PosReserveActionModal from './PosReserveActionModal';
import PosPager from './PosPager';
import PosProductTable from './PosProductTable';
import PosReplenishModal from './PosReplenishModal';
import PosProductModal from './PosProductModal';
import PosCategoryIconModal from './PosCategoryIconModal';
import Modal from './Modal';
import '../css/Pos.css';

/**
 * Inventory — the maintenance screen.
 *
 * Two sections, per the brief: categories (a plain list of strings the admin
 * curates) and products, filtered by category tab and a live search.
 *
 * THE PRODUCTS SECTION HAS TWO VIEWS of the same list. Cards are picture-led,
 * for finding something you would recognise by its packaging; the grid is rows
 * of text with the stock count editable in place, for checking a shelf against
 * a delivery note. Both read the same filtered, sorted list, so switching
 * between them never changes which products are on screen.
 *
 * @returns {JSX.Element}
 */
function PosInventory() {
  const { config, clientId } = useConfig();
  // "Can add stocks" is the WRITE permission on this screen. Visiting it and
  // changing what is on it are separate grants, so a role may open Inventory
  // to read stock levels without being able to register a product, retire a
  // category or correct a count.
  const { staffEmail, can } = usePosSession();
  const canEditStock = can('addStock');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  // name -> icon, from its own node. Absent for a client that has never set
  // one, which simply leaves every card on the default mark.
  const [categoryIcons, setCategoryIcons] = useState({});
  // The category whose icon is being picked, or null.
  const [iconFor, setIconFor] = useState(null);
  const [activeCategory, setActiveCategory] = useState(POS_ALL_CATEGORIES);
  const [search, setSearch] = useState('');
  const [view, setView] = useState(POS_INVENTORY_VIEW_DEFAULT);
  // Which STOCK is being looked at: the shelf, or the storeroom behind it.
  const [chosenScope, setScope] = useState(POS_STOCK_SCOPE_DEFAULT);
  // WITHOUT A RESERVE there is one stock, the active one, and every tab and
  // control about the storeroom is hidden. Derived rather than forced into
  // state, so switching the setting back on restores the tab that was open.
  const reserveOn = posReserveEnabled(config);
  const scope = reserveOn ? chosenScope : 'active';
  // The product being moved from reserve to shelf, or null. 'pick' opens the
  // same dialog with a product chooser instead — the Active tab's way in,
  // where there is no row to have started from.
  const [replenishing, setReplenishing] = useState(null);
  // The reserve product whose card was tapped, while its action is chosen.
  const [actionFor, setActionFor] = useState(null);
  // Opens on the sort this client last picked here, and remembers changes.
  const {
    sortKey, sortDir, loaded: sortLoaded, changeSort, toggleDirection,
  } = useSavedSort({ clientId, screen: POS_SORT_PREFERENCE_SCREENS.INVENTORY, actor: staffEmail });
  // Units sold per product, for the "Items sold" order. Null until the orders
  // have been read, which only happens if that order is chosen — see below.
  const [unitsSold, setUnitsSold] = useState(null);
  // 1-based. Reset whenever the list underneath changes — see below.
  const [page, setPage] = useState(1);
  // null = closed, 'new' = register, an object = edit that product.
  const [editing, setEditing] = useState(null);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const stopCategories = subscribePosCategories(clientId, setCategories);
    const stopIcons = subscribePosCategoryIcons(clientId, setCategoryIcons);
    const stopProducts = subscribePosProducts(clientId, (list) => {
      setProducts(list);
      setLoading(false);
    });
    return () => { stopCategories(); stopIcons(); stopProducts(); };
  }, [clientId]);

  // Orders are read ONLY while the list is ordered by what has sold, for the
  // same reason as on the till: the orders node is the heaviest one a client
  // has, and nobody should pay for it to answer a question they did not ask.
  useEffect(() => {
    if (sortKey !== POS_SORT_BY_SOLD) return undefined;
    const stop = subscribePosOrders(clientId, (orders) => setUnitsSold(tallyPosUnitsSold(orders)));
    return stop;
  }, [clientId, sortKey]);

  const isCustomSort = sortKey === POS_SORT_BY_CUSTOM;
  const customSort = useCustomSort({
    clientId,
    listKey: POS_CUSTOM_SORT_LISTS.SHARED,
    active: isCustomSort,
    products,
    actor: staffEmail,
  });
  // Arranging is a write, so it follows the same right as every other change
  // on this screen.
  const arranging = isCustomSort && canEditStock;
  const { draggingId, getDragProps } = useDragReorder({ enabled: arranging, onMove: customSort.move });

  /**
   * Saves the dragged arrangement, reporting a failure in the modal.
   * @returns {Promise<void>}
   */
  const handleSaveCustomSort = async () => {
    try {
      await customSort.save();
    } catch (error) {
      setModal({ title: 'Could not save the order', message: error.message, type: 'error' });
    }
  };

  /**
   * Category tab + search, applied together.
   *
   * Search runs from the FIRST character (POS_SEARCH_MIN_CHARS) across name
   * and description, and is scoped to the active tab rather than replacing
   * it — the tab is where you are, the search narrows it.
   */
  const visibleProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = products.filter((product) => {
      if (activeCategory !== POS_ALL_CATEGORIES && product.category !== activeCategory) return false;
      if (term.length < POS_SEARCH_MIN_CHARS) return true;
      return `${product.name || ''} ${product.description || ''}`.toLowerCase().includes(term);
    });
    // Sorted after filtering, and into a NEW array — sortPosProducts never
    // reorders the list React is holding in state.
    return sortPosProducts(matching, {
      key: sortKey, direction: sortDir, unitsSold, scope, customOrder: customSort.order,
    });
  }, [products, activeCategory, search, sortKey, sortDir, unitsSold, scope, customSort.order]);


  // 0 means show everything, which is the default. Config -> Product Settings
  // sets it per client, and it applies to BOTH views here — switching between
  // grid and card must not change which products are on the page.
  const pageSize = posProductPageSize(config);

  // Back to page 1 whenever the list underneath changes. The view is
  // deliberately NOT in this list: it draws the same products differently, so
  // switching it should leave you where you were.
  useEffect(() => { setPage(1); }, [activeCategory, search, sortKey, sortDir, pageSize]);

  // Clamped as well, because the list can shrink without a control being
  // touched — a product removed, or sold out on a till elsewhere.
  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(visibleProducts.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount);

  const pagedProducts = useMemo(() => (
    pageSize > 0
      ? visibleProducts.slice((safePage - 1) * pageSize, safePage * pageSize)
      : visibleProducts
  ), [visibleProducts, pageSize, safePage]);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await addPosCategory(clientId, newCategory, staffEmail);
      setNewCategory('');
    } catch (error) {
      setModal({ title: 'Could not add category', message: error.message, type: 'error' });
    }
  };

  /**
   * Saves (or clears) one category's icon and closes the picker.
   *
   * @param {string} icon - emoji, Iconify id, or '' to clear
   * @returns {Promise<void>}
   */
  const handleSaveIcon = async (icon) => {
    const name = iconFor;
    setIconFor(null);
    try {
      await setPosCategoryIcon(clientId, name, icon, staffEmail);
    } catch (error) {
      setModal({ title: 'Could not save the icon', message: error.message, type: 'error' });
    }
  };

  /**
   * One category's icon as the till will draw it.
   *
   * An Iconify id becomes a CSS mask rather than an <img>, so it takes the
   * surrounding colour and no third-party markup enters the DOM. Returns null
   * when the category has no icon — the caller decides what to show instead.
   *
   * @param {string} name - the category
   * @returns {JSX.Element|null}
   */
  const renderCategoryIcon = (name) => {
    const icon = categoryIcons?.[name];
    if (!icon) return null;
    if (!posIconIsIconify(icon)) return <span aria-hidden="true">{icon}</span>;
    return (
      <span
        className="pos-icon-mask"
        style={{ WebkitMaskImage: `url(${posIconifyUrl(icon)})`, maskImage: `url(${posIconifyUrl(icon)})` }}
        aria-hidden="true"
      />
    );
  };

  const handleRemoveCategory = (name) => {
    const inUse = products.filter(p => p.category === name).length;
    setModal({
      title: `Remove "${name}"?`,
      // Says what happens to the products rather than leaving it to be
      // discovered: nothing is deleted, they simply lose this tab.
      message: inUse > 0
        ? `${inUse} product${inUse === 1 ? '' : 's'} use this category. They are not deleted — they keep the category name and stay visible under All.`
        : 'No products use this category.',
      type: 'warning',
      confirmText: 'Remove',
      showCancel: true,
      cancelText: 'Keep it',
      onConfirm: async () => {
        setModal(null);
        try {
          await removePosCategory(clientId, name, staffEmail);
          if (activeCategory === name) setActiveCategory(POS_ALL_CATEGORIES);
        } catch (error) {
          setModal({ title: 'Error', message: error.message, type: 'error' });
        }
      },
    });
  };

  if (loading || !sortLoaded) {
    return <div className="pos-loading">{spinnerRing('Loading inventory…')}</div>;
  }

  const isReserve = scope === 'reserve';

  return (
    <div className="pos-inventory">
      {/* ---------- The two stocks ----------
          Top level, above everything, because it changes what every section
          below is ABOUT — not a filter on one list but a different question:
          what is on the shelf, or what is still in the storeroom. Absent
          entirely when this client keeps no reserve. */}
      {reserveOn && (
      <div className="pos-scopetabs" role="tablist" aria-label="Stock">
        {POS_STOCK_SCOPES.map((option) => (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={scope === option.key}
            className={`pos-scopetab${scope === option.key ? ' active' : ''}`}
            onClick={() => setScope(option.key)}
          >
            <span className="pos-scopetab-label">{option.label}</span>
            <span className="pos-scopetab-desc">{option.description}</span>
          </button>
        ))}
      </div>
      )}

      {/* ---------- Section 1: categories ---------- */}
      {/* Compact: a list of short names does not need a full-size panel. */}
      <section className="pos-section pos-section-compact">
        <div className="pos-section-head">
          <h2>Categories</h2>
          <p>Used to group products and to build the filter tabs below.</p>
        </div>

        {canEditStock && (
        <form className="pos-category-form" onSubmit={handleAddCategory}>
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="e.g. Drinks"
            aria-label="New category name"
          />
          <button type="submit" className="pos-btn pos-btn-primary" disabled={!newCategory.trim()}>
            <Plus size={15} /> Add
          </button>
        </form>
        )}

        {categories.length === 0 ? (
          <p className="pos-empty-line">No categories yet.</p>
        ) : (
          <ul className="pos-category-list">
            {categories.map((name) => (
              <li key={name} className="pos-category-chip">
                {/* The icon slot doubles as the way to change it — there is no
                    room on a chip for a separate "edit icon" control, and the
                    icon is the thing you would aim at anyway. Empty until one
                    is chosen, so a club that wants none sees no clutter. */}
                {canEditStock ? (
                  <button
                    type="button"
                    className={`pos-category-icon${categoryIcons?.[name] ? ' set' : ''}`}
                    onClick={() => setIconFor(name)}
                    aria-label={categoryIcons?.[name] ? `Change the icon for ${name}` : `Add an icon to ${name}`}
                    title={categoryIcons?.[name] ? 'Change icon' : 'Add icon'}
                  >
                    {renderCategoryIcon(name) || <Plus size={11} />}
                  </button>
                ) : (
                  renderCategoryIcon(name) && (
                    <span className="pos-category-icon set">{renderCategoryIcon(name)}</span>
                  )
                )}
                <span>{name}</span>
                {canEditStock && (
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    title={`Remove ${name}`}
                    onClick={() => handleRemoveCategory(name)}
                  >
                    <X size={13} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---------- Section 2: products ---------- */}
      <section className="pos-section">
        <div className="pos-section-head pos-section-head-row pos-inv-head">
          <div>
            {/* Names the stock being listed, so the heading still says which
                one you are on after the tabs have scrolled off the top. */}
            <h2>{!reserveOn ? 'Stock Inventory' : (isReserve ? 'Reserve Stocks Inventory' : 'Active Stocks Inventory')}</h2>
            <p>
              {products.length} registered.
              {!reserveOn
                ? ' Register products and add stock here.'
                : (isReserve
                  ? ' Register products here, then replenish to move stock to the shelf.'
                  : ' The shelf is filled from reserve — adding stock here takes it out of the reserve.')}
            </p>
          </div>
          {/* REGISTERING HAPPENS ON THE RESERVE TAB, because that is where a
              product enters this system: stock is bought into the storeroom
              and carried to the shelf from there. The Active tab therefore has
              nothing to register — only stock to bring forward, which is what
              its button does. */}
          {canEditStock && (isReserve || !reserveOn ? (
            <button type="button" className="pos-btn pos-btn-primary" onClick={() => setEditing('new')}>
              <Plus size={15} /> Add new product
            </button>
          ) : (
            <button
              type="button"
              className="pos-btn pos-btn-primary"
              onClick={() => setReplenishing('pick')}
              disabled={!products.some(p => posReserveUnits(p) > 0)}
              title={products.some(p => posReserveUnits(p) > 0)
                ? undefined
                : 'Nothing in reserve yet — register a product on the Reserve Stocks tab first'}
            >
              <Plus size={15} /> Add from reserve
            </button>
          ))}
        </div>

        <div className="pos-filter-bar">
          <div className="pos-cat-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeCategory === POS_ALL_CATEGORIES}
              className={`pos-cat-tab${activeCategory === POS_ALL_CATEGORIES ? ' active' : ''}`}
              onClick={() => setActiveCategory(POS_ALL_CATEGORIES)}
            >
              All
            </button>
            {categories.map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={activeCategory === name}
                className={`pos-cat-tab${activeCategory === name ? ' active' : ''}`}
                onClick={() => setActiveCategory(name)}
              >
                {name}
              </button>
            ))}
          </div>

          <div className="pos-filter-tools">
            <div className="pos-search">
              <Search size={15} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or description"
                aria-label="Search products"
              />
            </div>

            <PosSortBar
              sortKey={sortKey}
              direction={sortDir}
              onSortChange={changeSort}
              onDirectionToggle={toggleDirection}
              onSaveCustom={canEditStock ? handleSaveCustomSort : undefined}
              customDirty={customSort.dirty}
              customSaving={customSort.saving}
            />

            {/* Which VIEW, not which products — so it sits apart from the
                category tabs above, which do change what is listed. */}
            <div className="pos-viewtabs" role="tablist" aria-label="Product view">
              {POS_INVENTORY_VIEWS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  role="tab"
                  aria-selected={view === option.key}
                  className={`pos-viewtab${view === option.key ? ' active' : ''}`}
                  onClick={() => setView(option.key)}
                  title={option.description}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {arranging && visibleProducts.length > 0 && (
          <p className="pos-arrange-hint">
            {view === 'grid'
              ? 'Drag the rows into the order you want, then tap Save. The order applies to both Products and Inventory.'
              : 'Drag the cards into the order you want (press and hold on a touch screen), then tap Save. The order applies to both Products and Inventory.'}
          </p>
        )}

        {visibleProducts.length === 0 ? (
          <p className="pos-empty-line">
            {products.length === 0
              ? (canEditStock
                ? (isReserve || !reserveOn
                  ? 'No products yet. Use "Add new product" to register the first one.'
                  : 'No products yet. Register the first one on the Reserve Stocks tab.')
                : 'No products yet. Ask someone who can add stock to register the first one.')
              : 'Nothing matches this filter.'}
          </p>
        ) : (
        <>
        {view === 'grid' ? (
          <PosProductTable
            products={pagedProducts}
            clientId={clientId}
            staffEmail={staffEmail}
            canEdit={canEditStock}
            onEdit={setEditing}
            scope={scope}
            onReplenish={setActionFor}
            getDragProps={getDragProps}
            draggingId={draggingId}
            onError={(error) => setModal({ title: 'Could not update the stock', message: error.message, type: 'error' })}
          />
        ) : (
          <div
            className={`pos-product-grid ${posCardGridAttrs(config).className}${arranging ? ' pos-grid-arrange' : ''}${arranging && customSort.wiggle ? ' pos-grid-unsaved' : ''}`}
            style={posCardGridAttrs(config).style}
          >
            {pagedProducts.map((product) => (
              // ON THE RESERVE TAB THE WHOLE CARD IS THE CONTROL: tapping it
              // asks whether to replenish the shelf or edit the reserve.
              <article
                key={product.id}
                className={`pos-product-card${isReserve && canEditStock ? ' pos-product-card-action' : ''}${draggingId === product.id ? ' dragging' : ''}`}
                {...(isReserve && canEditStock ? {
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': `Choose an action for ${product.name}`,
                  onClick: () => setActionFor(product),
                  onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActionFor(product); }
                  },
                } : {})}
                {...getDragProps(product.id)}
              >
                {/* Section 1 — the picture, in a square frame it cannot
                    resize. Its contents are absolutely positioned, so however
                    tall the uploaded photo is it can never push into the
                    details below. The category banner floats in the corner. */}
                <div className="pos-product-media">
                  {product.imageUrl
                    ? <img src={product.imageUrl} alt="" />
                    : <span className="pos-product-noimage" aria-hidden="true">No image</span>}
                  {product.category && <span className="pos-product-cat">{product.category}</span>}
                </div>

                {/* Section 2 — the details. */}
                <div className="pos-product-body">
                  <h3 className="pos-product-name">{product.name}</h3>
                  {/* No description on the card: its varying length made cards
                      in one row different heights. It is shown in Grid view. */}
                  <div className="pos-product-foot">
                    {/* Same flag the grid carries, for the same reason. */}
                    {!isReserve && !(Number(product.price) > 0) ? (
                      <span className="pos-product-price pos-invtable-noprice">No price</span>
                    ) : (
                      <span className="pos-product-price">{formatMoney(Number(product.price || 0), config)}</span>
                    )}
                    {/* On the shelf this reads as a warning once it is running
                        low, and louder still at zero. In the storeroom there is
                        nothing to warn about — a reserve is meant to sit there
                        — so it is a plain figure in packs and pieces. */}
                    {isReserve ? (
                      <span className="pos-product-reserve">
                        {describePosUnits(posReserveUnits(product), product)}
                      </span>
                    ) : (
                      <span className={`pos-product-stock ${posStockLevel(product, config?.lowStockThreshold)}`}>
                        {Number(product.supplies) || 0} in stock
                      </span>
                    )}
                  </div>
                </div>
                {canEditStock && (isReserve ? (
                  // Visual cue only — the whole card is the control, so this
                  // is not a second button nested inside it.
                  <span className="pos-product-edit" aria-hidden="true">
                    Manage stock
                  </span>
                ) : (
                  <button
                    type="button"
                    className="pos-product-edit"
                    data-no-drag
                    onClick={() => setEditing(product)}
                    aria-label={`Edit ${product.name}`}
                  >
                    <Pencil size={14} /> Edit
                  </button>
                ))}
              </article>
            ))}
          </div>
        )}

        {/* Outside the view branch, so the pager is the same control in both
            and stays put when you switch between them. */}
        {pageSize > 0 && (
          <PosPager
            page={safePage}
            totalItems={visibleProducts.length}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        )}
        </>
        )}
      </section>

      {editing && (
        <PosProductModal
          scope={scope}
          // Keyed so switching from one product to another remounts it. The
          // form holds stock in its own state (a delivery recorded there does
          // not come back through the live product list), and reusing the
          // instance would carry one product's figure onto another's.
          key={editing === 'new' ? 'new' : editing.id}
          product={editing === 'new' ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
        />
      )}

      {/* Reads the product out of the LIVE list rather than holding the one
          that was clicked: the figures it shows and checks against must be the
          current ones, not a snapshot from whenever the button was pressed. */}
      {replenishing && (
        <PosReplenishModal
          // 'pick' hands it the whole list to choose from; a product hands it
          // that one. Either way it reads from the LIVE list rather than a
          // snapshot, so the figures it checks against are current.
          {...(replenishing === 'pick'
            ? { products }
            : { product: products.find(p => p.id === replenishing.id) || replenishing })}
          onClose={() => setReplenishing(null)}
        />
      )}

      {actionFor && (
        <PosReserveActionModal
          product={products.find(p => p.id === actionFor.id) || actionFor}
          onReplenish={() => { setReplenishing(actionFor); setActionFor(null); }}
          onEdit={() => { setEditing(actionFor); setActionFor(null); }}
          onClose={() => setActionFor(null)}
        />
      )}

      {iconFor && (
        <PosCategoryIconModal
          categoryName={iconFor}
          value={categoryIcons?.[iconFor] || ''}
          onSave={handleSaveIcon}
          onClose={() => setIconFor(null)}
        />
      )}

      <Modal
        isOpen={!!modal}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText={modal?.confirmText || 'OK'}
        showCancel={modal?.showCancel}
        cancelText={modal?.cancelText}
        onConfirm={modal?.onConfirm}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosInventory;
